import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Gift, DollarSign, Users, ArrowRight } from "lucide-react";
import VoltIcon from "./VoltIcon";

/**
 * ReferralBanner — Reliant/Rhythm-tier corporate layout.
 *
 * Layout:
 *   • Desktop: copy LEFT, image RIGHT (50/50)
 *   • Mobile : image TOP, copy BOTTOM (no order swap, just flex-col)
 *
 * Typography is sans-serif extrabold (Inter) for a corporate feel that
 * matches the visual references the user shared. Titles split with a
 * green-accent second line.
 */
const FEATURES = [
  { icon: DollarSign, key: "feat1" },
  { icon: Users, key: "feat2" },
  { icon: VoltIcon, key: "feat3" },
];

export default function ReferralBanner() {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith("en");
  const targetPath = isEn ? "/referrals" : "/referidos";

  return (
    <section className="relative bg-white" data-testid="referral-banner">
      <div className="max-w-[1180px] mx-auto px-5 py-16 md:py-24">
        <div className="flex flex-col-reverse md:grid md:grid-cols-[minmax(0,1.05fr)_minmax(0,0.95fr)] md:gap-12 lg:gap-16 items-stretch gap-10">
          {/* Copy */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="w-full"
          >
            {/* Kicker pill */}
            <span
              className="inline-flex items-center gap-1.5 rounded-full bg-velcor-green/10 px-3 py-1.5 text-velcor-green mb-5"
              style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.16em" }}
            >
              <Gift className="w-3 h-3" strokeWidth={2.4} />
              {t("home.referralKicker")}
            </span>

            {/* Title — Fraunces serif (matches hero) but heavier weight */}
            <h2
              className="display text-velcor-ink leading-[1.06]"
              style={{
                fontSize: "clamp(28px, 4.4vw, 48px)",
                fontWeight: 600,
                letterSpacing: "-0.02em",
              }}
              data-testid="referral-banner-title"
            >
              {t("home.referralTitle")}{" "}
              <span className="text-velcor-green">{t("home.referralTitleAccent")}</span>
            </h2>

            <p
              className="mt-5 text-slate-600 leading-relaxed max-w-[480px]"
              style={{ fontSize: 15.5, fontWeight: 400 }}
            >
              {t("home.referralLead1")}{" "}
              <span className="text-velcor-ink font-extrabold">{t("home.referralAmount")}</span>{" "}
              {t("home.referralLead2")}
            </p>

            {/* Feature list with green circle icons + hairlines */}
            <ul className="mt-8 max-w-[520px]" data-testid="referral-features">
              {FEATURES.map((f, i) => (
                <li
                  key={f.key}
                  className={`flex items-start gap-4 py-4 ${i < FEATURES.length - 1 ? "border-b border-slate-100" : ""}`}
                  data-testid={`referral-${f.key}`}
                >
                  <span className="grid place-items-center w-10 h-10 rounded-full bg-velcor-green/10 text-velcor-green shrink-0">
                    <f.icon className="w-4 h-4" strokeWidth={2.4} />
                  </span>
                  <div className="min-w-0">
                    <h3
                      className="text-velcor-ink leading-tight tracking-tight"
                      style={{ fontSize: 16, fontWeight: 700 }}
                    >
                      {t(`home.referral_${f.key}_title`)}
                    </h3>
                    <p
                      className="mt-1 text-slate-500 leading-relaxed"
                      style={{ fontSize: 13.5, fontWeight: 400 }}
                    >
                      {t(`home.referral_${f.key}_body`)}
                    </p>
                  </div>
                </li>
              ))}
            </ul>

            {/* Big green CTA button */}
            <Link
              to={targetPath}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-velcor-green text-white pl-3 pr-6 py-3.5 hover:bg-velcor-green/95 active:scale-[0.98] transition-all duration-200 shadow-[0_18px_36px_rgba(47,122,58,0.28)]"
              data-testid="referral-banner-cta"
            >
              <span className="grid place-items-center w-8 h-8 rounded-full bg-white/15">
                <Gift className="w-4 h-4" strokeWidth={2.4} />
              </span>
              <span className="font-extrabold text-[14.5px]">{t("home.referralCta")}</span>
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>

          {/* Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="relative w-full md:h-full"
          >
            <div
              className="relative overflow-hidden rounded-[28px] aspect-[4/5] md:aspect-auto md:h-full md:min-h-[480px] shadow-[0_28px_60px_rgba(15,23,42,0.12)] group w-full"
            >
              <img
                src="/referral-share.png"
                alt={t("home.referralImageAlt")}
                className="w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                loading="lazy"
                data-testid="referral-banner-image"
              />
              <div className="absolute inset-0 ring-1 ring-black/5 rounded-[28px] pointer-events-none" />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
